/**
 * The "Explore other Capabilities and Solutions" section near the end of capability, solution and
 * service pages: a list of cards, each an image, a linked heading, a coloured badge
 * ("Capabilities", "Solution") and a short description, then a "Learn more" link.
 *
 * Rendered by `../BasePage/BasePage.tsx` and 18 pages under `pages/metal-solutions/` (the
 * bar, tube & structural and plate processing sub-pages, the Ryerson solutions pages, and the
 * Southern Tool Steel services and solutions pages), always as an `AllSections` entry. Their copy
 * lives in the content modules, typed with {@link ExploreOtherCapabilitiesSolutionsProps}, for
 * example
 * `src/content/ryerson/metal-solutions/capabilities/bar-tube-structural-processing/sawing.ts` →
 * `ExploreOtherCapabilitiesSolutionsContent`. A change to these types reaches all of them.
 *
 * From `lg` up it renders `./Desktop/ExploreOtherCapabilitesDesktop.tsx` (with the title as a
 * heading) on the secondary background; below `lg`, `./Mobile/ExploreOtherCapabilitesMobile.tsx`
 * (no title: the `AllSections` accordion tab shows it).
 */
import styled from "@emotion/styled";
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection } from "@ryerson/frontend.layout";
import { css } from "@emotion/react";
import { Media } from "@ryerson/frontend.next";
import ExploreOtherCapabilitiesSolutionsDesktop from "../ExploreOtherCapabilitiesSolutions/Desktop/ExploreOtherCapabilitesDesktop";
import ExploreOtherCapabilitiesSolutionsMobile from "../ExploreOtherCapabilitiesSolutions/Mobile/ExploreOtherCapabilitesMobile";

/**
 * Badge colour: `success`, `error` and `industry` map to the theme's success, error and secondary
 * brand colours; `other` (or none) is the secondary orange.
 */
type badgeColor = "success" | "error" | "industry" | "other";
/** One card. */
export interface ExploreOtherCapabilitiesSolutionsSection {
	/** Card image, a site-relative path. The image gets no `alt` text (see the desktop file). */
	imageUrl: string;
	/** Card heading, linked to `linkUrl`. */
	mainText: string;
	subText: string;
	/** Badge label, for example "Capabilities" or "Solution". */
	badgeText: string;
	badgeColor?: badgeColor;
	/**
	 * Target of the heading link. Without it the shared `Link` renders no children, so the card
	 * has no heading.
	 */
	linkUrl?: string;
}
/** Copy for {@link ExploreOtherCapabilitiesSolutions}. */
export interface ExploreOtherCapabilitiesSolutionsProps {
	section: ExploreOtherCapabilitiesSolutionsSection[];
	/** Section title: the desktop heading and, on the pages, the `AllSections` title and anchor. */
	title: string;
	/**
	 * Label of the closing "Learn more" link. The untyped `pageContent` objects that
	 * `../BasePage/BasePage.tsx` reads pass a two-element array instead, which `ButtonLink` draws
	 * as two lines.
	 */
	learnMoreText?: string;
	learnMoreUrl?: string;
}
const RootContainer = styled(ContentSection)`
	max-width: 1440px;
	width: 100%;
	min-height: 700px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.xl}) {
				min-height: 750px;
			}
		`;
	}}
`;

/** The explore-other cards section; see the file header. */
const ExploreOtherCapabilitiesSolutions: React.FC<ExploreOtherCapabilitiesSolutionsProps> = ({
	...props
}) => {
	const { theme } = useTheme();
	return (
		<>
			<Media greaterThanOrEqual="lg">
				<RootContainer theme={theme} type="secondary">
					<ExploreOtherCapabilitiesSolutionsDesktop {...props} />
				</RootContainer>
			</Media>
			<Media lessThan="lg">
				<ExploreOtherCapabilitiesSolutionsMobile {...props} />
			</Media>
		</>
	);
};

export default ExploreOtherCapabilitiesSolutions;
